import { Participant } from '../../components/diagram-canvas/models/participant.class';
import { BASE_Y, HALF_PARTICIPANT_WIDTH } from '../../components/diagram-canvas/constants';
import {
  ARROW_HEAD_ANGLE,
  ARROW_HEAD_SIZE,
  ARROW_REGEX,
  ARROW_VERTICAL_DIFF,
  GAP_BETWEEN_MESSAGES,
  MARGIN_BEFORE_FIRST_ARROW,
  MESSAGE_LABEL_OFFSET,
  MIDPOINT,
} from '../../components/diagram-canvas/constants';
import { Injectable } from '@angular/core';
import { DrawMessageOptions } from '../../components/diagram-canvas/models/diagram.model';

@Injectable({ providedIn: 'root' })
export class MessageDrawerService {
  drawAll(lines: string[], ctx: CanvasRenderingContext2D, participants: Participant[]): void {
    let messageY = BASE_Y + MARGIN_BEFORE_FIRST_ARROW;
    for (const line of lines) {
      const match = line.match(ARROW_REGEX);
      if (match) {
        const [, from, to, label] = match;
        messageY = this.drawMessage({
          ctx,
          participants,
          fromName: from,
          toName: to,
          label,
          y: messageY,
        });
      }
    }
  }

  /**
   * Draws a single message (arrow and label) between two participants on the sequence diagram.
   *
   * @param options - Object containing canvas context, participant array, sender and receiver names, label, and current Y position.
   * @returns The updated Y position to draw the next message below without overlap.
   */
  drawMessage(options: DrawMessageOptions): number {
    const { ctx, participants, fromName, toName, label, y } = options;

    const fromParticipant = participants.find(p => p.name === fromName);
    const toParticipant = participants.find(p => p.name === toName);

    if (!fromParticipant || !toParticipant) {
      return y + GAP_BETWEEN_MESSAGES;
    }

    const fromX = this._getParticipantCenterX(fromParticipant, ctx);
    const toX = this._getParticipantCenterX(toParticipant, ctx);

    ctx.beginPath();
    ctx.moveTo(fromX, y);
    ctx.lineTo(toX, y);
    ctx.stroke();

    const angle = Math.atan2(ARROW_VERTICAL_DIFF, toX - fromX);

    ctx.beginPath();
    ctx.moveTo(toX, y);
    ctx.lineTo(toX - ARROW_HEAD_SIZE * Math.cos(angle - ARROW_HEAD_ANGLE), y - ARROW_HEAD_SIZE * Math.sin(angle - ARROW_HEAD_ANGLE));
    ctx.moveTo(toX, y);
    ctx.lineTo(toX - ARROW_HEAD_SIZE * Math.cos(angle + ARROW_HEAD_ANGLE), y - ARROW_HEAD_SIZE * Math.sin(angle + ARROW_HEAD_ANGLE));
    ctx.stroke();

    ctx.fillText(label, (fromX + toX) / MIDPOINT, y - MESSAGE_LABEL_OFFSET);

    return y + GAP_BETWEEN_MESSAGES; // Returns updated Y position for the next message.
  }

  private _getParticipantCenterX(participant: Participant, ctx: CanvasRenderingContext2D): number {
    if (participant.type === 'actor') {
      return participant.x + HALF_PARTICIPANT_WIDTH;
    }
    const textWidth = ctx.measureText(participant.name).width;
    const boxWidth = Math.max(100, textWidth + 20);
    return participant.x + boxWidth / 2;
  }
}
